<template>
  <dl class="detail-list">
    <div
      v-for="(row, rowIndex) in rows"
      :key="row.key || row.field || `row-${rowIndex}`"
      :class="[
        'detail-list__row',
        { 'detail-list__row--first': rowIndex === 0 && !hideFirstRowTopBorder },
        { 'detail-list__row--muted': toggleSwitchState && row.conditional },
      ]"
    >
      <dt class="detail-list__term">
        <component
          v-if="isComponentValue(row.col1)"
          :is="row.col1.component"
          v-bind="row.col1.props"
        />
        <template v-else>{{ textValue(row.col1) }}</template>
      </dt>
      <dd class="detail-list__description">
        <component
          v-if="isComponentValue(row.col2)"
          :is="row.col2.component"
          v-bind="row.col2.props"
        />
        <template v-else-if="isTextValue(row.col2)">
          <span>{{ row.col2.text }}</span>
          <UnitButton
            v-if="showReadMoreButton && row.col2.button"
            variant="text"
            size="micro"
            @click="emit('toggle-description')"
          >
            {{ row.col2.button }}
          </UnitButton>
        </template>
        <template v-else>{{ textValue(row.col2) }}</template>

        <UnitButton
          v-if="canEdit(row)"
          variant="text"
          size="micro"
          @click="editRow(rowIndex)"
          v-haptic
        >
          {{ $t("buttons.edit") }}
        </UnitButton>
        <span v-else-if="editState(row) === 'Auto'">Auto</span>
      </dd>
    </div>
  </dl>
</template>

<script setup>
import UnitButton from "./UnitButton.vue";

defineOptions({
  name: "DetailList",
});

const props = defineProps({
  rows: {
    type: Array,
    required: true,
  },
  toggleSwitchState: {
    type: Boolean,
    default: false,
  },
  showEditButton: {
    type: [Function, String, Boolean],
    default: false,
  },
  hideFirstRowTopBorder: {
    type: Boolean,
    default: false,
  },
  showReadMoreButton: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["edit-row", "toggle-description"]);
const isComponentValue = (value) =>
  Boolean(value && typeof value === "object" && value.component);
const isTextValue = (value) =>
  Boolean(value && typeof value === "object" && typeof value.text === "string");
const textValue = (value) => {
  if (value === null || value === undefined) return "—";
  if (["string", "number", "boolean"].includes(typeof value)) return String(value);
  return "—";
};
const editState = (row) =>
  typeof props.showEditButton === "function"
    ? props.showEditButton(row)
    : props.showEditButton;
const canEdit = (row) =>
  editState(row) === true && row.field !== "allowTrades";
const editRow = (rowIndex) => {
  const row = props.rows[rowIndex];
  emit("edit-row", { index: rowIndex, field: row.field || row.col1 });
};
</script>

<style scoped>
.detail-list {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: var(--space-5) 0;
}

.detail-list__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: var(--space-5);
  min-height: calc(var(--control-height-default) + 2px);
  padding: var(--space-5) 0;
  border-bottom: 1px solid var(--color-border-subtle);
}

.detail-list__row--first {
  border-top: 1px solid var(--color-border-subtle);
}

.detail-list__row--muted {
  color: var(--color-text-muted);
}

.detail-list__term,
.detail-list__description {
  min-width: 0;
  margin: 0;
  overflow-wrap: anywhere;
}

.detail-list__term {
  font-weight: var(--font-weight-medium);
}

.detail-list__description {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-5);
}

@media (max-width: 530px) {
  .detail-list__row {
    grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
  }
}
</style>
